import { Button, Input, Label } from '@xhubio-saas/core-frontend/ui-components'
import { Plus, Trash2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { EMPTY_MANUAL_ITEM, MANUAL_ITEM_LIMITS, type ManualItem } from './manual-items'

const ROW_CLASS = 'grid grid-cols-12 items-end gap-2 border-b border-border/50 pb-3'

export interface ManualItemsEditorProps {
  items: ManualItem[]
  onChange: (next: ManualItem[]) => void
}

/** Positionen von Hand (E19): Beschreibung, Menge, Einzelpreis, USt-Satz. Die Summen rechnet der Prüfschritt. */
export function ManualItemsEditor({ items, onChange }: ManualItemsEditorProps) {
  const { t } = useTranslation()

  const change = (index: number, field: keyof ManualItem, value: string) => {
    onChange(items.map((item, i) => (i === index ? { ...item, [field]: value } : item)))
  }

  const field = (index: number, name: keyof ManualItem, className: string) => {
    const id = `invoice-from-pdf-item-${name}-${index}`
    const isPeriod = name === 'periodStart' || name === 'periodEnd'
    return (
      <div className={`space-y-1 ${className}`}>
        <Label htmlFor={id} className="text-xs text-muted-foreground">
          {t(`invoiceFromPdf.review.items.${name}`)}
        </Label>
        <Input
          id={id}
          value={items[index]?.[name] ?? ''}
          maxLength={MANUAL_ITEM_LIMITS[name]}
          type={isPeriod ? 'date' : 'text'}
          inputMode={name === 'description' || isPeriod ? undefined : 'decimal'}
          className={`h-9 ${name === 'description' || isPeriod ? '' : 'text-right tabular-nums'}`}
          data-testid={id}
          onChange={(e) => change(index, name, e.target.value)}
        />
      </div>
    )
  }

  return (
    <div className="space-y-3" data-testid="invoice-from-pdf-items-editor">
      {items.map((_, index) => (
        // biome-ignore lint/suspicious/noArrayIndexKey: Positionen haben keine Kennung, die Reihenfolge ist die Identität
        <div key={index} className={ROW_CLASS}>
          {field(index, 'description', 'col-span-12')}
          {field(index, 'quantity', 'col-span-3')}
          {field(index, 'unitPrice', 'col-span-4')}
          {field(index, 'taxRate', 'col-span-5')}
          {field(index, 'periodStart', 'col-span-5')}
          {field(index, 'periodEnd', 'col-span-5')}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="col-span-2 justify-self-end"
            disabled={items.length === 1}
            aria-label={t('invoiceFromPdf.review.items.remove', { n: index + 1 })}
            onClick={() => onChange(items.filter((__, i) => i !== index))}
          >
            <Trash2 className="h-4 w-4" aria-hidden="true" />
          </Button>
        </div>
      ))}
      <Button
        type="button"
        variant="outline"
        size="sm"
        data-testid="invoice-from-pdf-item-add"
        onClick={() => onChange([...items, EMPTY_MANUAL_ITEM])}
      >
        <Plus className="h-4 w-4" aria-hidden="true" />
        {t('invoiceFromPdf.review.items.add')}
      </Button>
    </div>
  )
}
